import React, { useContext, useEffect, useState, useRef } from "react";
import ControlLightColourWheel from "./ControlLightColourWheel";
import ControlLightAnimate from "./ControlLightAnimate";
import buttonBlueGlow from "../resources/button_blue_gradient_glow_small.png";
import glowCircleBg from "../resources/button_blue_gradient_circle_glow.png";
import { MainContext } from "../../context/MainContext";
import * as Constants from "../../components/api/constants";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";

const ControlLightMode = () => {
  const { state } = useContext(MainContext);
  const [mode, setMode] = useState("Single");
  const [lightOn, setLightOn] = useState(false);

  const [deviceId, setDeviceId] = useState(0);
  const [lightMode, setLightMode] = useState("Off");
  const [brightness, setBrightness] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [colour, setColour] = useState("");

  const colourChangeTimeout = useRef(null);
  const HUE_ROTATION_DEG = 90;

  const colourMap = {
    "00": "white",
    "01": "red",
    "02": "green",
    "03": "blue",
    "04": "orange",
    "05": "teal",
    "06": "purple",
    "16": "lime",
    "18": "pink",
  };

  useEffect(() => {
    if (!state?.R5 || !state?.R6) return;

    const r5 = state.R5.split(",");
    const r6 = state.R6.split(",");

    const colourIndex = parseInt(r6[3], 10);
    const formattedColour = colourIndex.toString().padStart(2, "0");

    const colourName = colourMap[formattedColour] || "unknown";
    setColour(colourName);

    setDeviceId(state.selectedDevice);

    if (r5[14] === "1") {
      switch (r6[4]) {
        case "1":
          setMode("Single");
          setLightMode("colour");
          break;
        case "2":
          setMode("Animate");
          setLightMode("fade");
          break;
        case "3":
          setMode("Animate");
          setLightMode("step");
          break;
        case "4":
          setMode("Animate");
          setLightMode("party");
          break;
        default:
          setMode("Single");
          setLightMode("off");
      }
    } else {
      setMode("Single");
      setLightMode("off");
    }

    setBrightness(r6[2]);
    setSpeed(r6[5]);

    setLightOn(r5[14] == "1");
  }, [state.R5, state.R6, state.selectedDevice]);

  const presetColorMap = {
    green: "#249B5C",
    blue: "#155BA9",
    teal: "#2BB5AE",
    purple: "#8F97CF",
    red: "#DE3352",
    pink: "#DD2E85",
    white: "#FDFEFE",
    lime: "#C8F247",
    orange: "#F58343",
  };

  function parseHslString(hslStr) {
    if (!hslStr || typeof hslStr !== "string") return null;
    const m = hslStr.match(/hsl\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*\)/i);
    if (!m) return null;
    return { h: Number(m[1]), s: Number(m[2]), l: Number(m[3]) };
  }

  function correctHue(h) {
    const corrected = (h + HUE_ROTATION_DEG) % 360;
    return corrected < 0 ? corrected + 360 : corrected;
  }

  function hslToRgbArray(h, s, l) {
    h = h % 360;
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const hh = h / 60;
    const x = c * (1 - Math.abs((hh % 2) - 1));
    let r1 = 0, g1 = 0, b1 = 0;

    if (0 <= hh && hh < 1) { r1 = c; g1 = x; b1 = 0; }
    else if (1 <= hh && hh < 2) { r1 = x; g1 = c; b1 = 0; }
    else if (2 <= hh && hh < 3) { r1 = 0; g1 = c; b1 = x; }
    else if (3 <= hh && hh < 4) { r1 = 0; g1 = x; b1 = c; }
    else if (4 <= hh && hh < 5) { r1 = x; g1 = 0; b1 = c; }
    else { r1 = c; g1 = 0; b1 = x; }

    const m = l - c / 2;
    const r = Math.round((r1 + m) * 255);
    const g = Math.round((g1 + m) * 255);
    const b = Math.round((b1 + m) * 255);
    return [r, g, b];
  }

  function colorDistance(r1, g1, b1, r2, g2, b2) {
    const dr = r1 - r2;
    const dg = g1 - g2;
    const db = b1 - b2;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  }

  function getNearestPresetColorFromHsl(hslString) {
    const parsed = parseHslString(hslString);
    if (!parsed) return "white";

    const correctedHue = correctHue(parsed.h);
    const [r1, g1, b1] = hslToRgbArray(correctedHue, parsed.s, parsed.l);

    let nearest = "white";
    let minDist = Infinity;
    for (const [name, hex] of Object.entries(presetColorMap)) {
      const r2 = parseInt(hex.slice(1, 3), 16);
      const g2 = parseInt(hex.slice(3, 5), 16);
      const b2 = parseInt(hex.slice(5, 7), 16);
      const dist = colorDistance(r1, g1, b1, r2, g2, b2);
      if (dist < minDist) {
        minDist = dist;
        nearest = name;
      }
    }

    return nearest;
  }

  const sendCommand = (command, value) => {
    console.log("Command:", command, "Value:", value);

    const requestOptions = {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        DeviceId: deviceId,
        Command: command,
        Value: value,
      }),
      credentials: "include",
    };

    fetch(Constants.baseURL + Constants.apiSendCommand, requestOptions)
      .then((response) => {
        if (response.ok) {
          toast.success("Command sent successfully. Refreshing...", {
            position: "top-right",
            autoClose: 10000,
          });
          setTimeout(() => state.refreshDeviceRF(deviceId), 6000);
          setTimeout(() => state.getUserDeviceRf(deviceId), 12000);
        } else {
          toast.error("Send failed. Please try again", { autoClose: 5000 });
        }
      })
      .catch((error) => console.log("request failed", error));
  };

  const handleColourChange = (hexColor) => {
    if (colourChangeTimeout.current) clearTimeout(colourChangeTimeout.current);
    colourChangeTimeout.current = setTimeout(() => {
      const nearest = getNearestPresetColorFromHsl(hexColor);
      sendCommand("lightColour", nearest);
    }, 500);
  };

  const handleBrightnessChange = (level) => {
    if (colourChangeTimeout.current) clearTimeout(colourChangeTimeout.current);
    colourChangeTimeout.current = setTimeout(() => {
      sendCommand("lightBrightness", level.toString());
    }, 300);
  };

  const handleAnimateMode = (mode) => {
    sendCommand("lightMode", mode);
  };

  const handleAnimateSpeedChange = (speedLevel) => {
    sendCommand("lightSpeed", speedLevel.toString());
  };

  const handleAnimateBrightnessChange = (brightnessLevel) => {
    sendCommand("lightBrightness", brightnessLevel.toString());
  };

  const handleToggleLight = () => {
    const newState = !lightOn;
    setLightOn(newState);
    sendCommand(newState ? "lightOn" : "lightOff");
  };

  return (
    <>
      <ToastContainer />

      {/* Title */}
      <Typography
        component="h2"
        variant="h6"
        sx={{ color: "white", fontWeight: 600, mb: 1 }}
      >
        Lights
      </Typography>

      <Paper
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.08)",
          width: "100%",
          position: "relative",
        }}
      >
        <div
          style={{
            width: "100%",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: "16px",
            padding: "20px",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              onClick={handleToggleLight}
              style={{
                position: "relative",
                width: "70px",
                height: "70px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <img
                src={glowCircleBg}
                alt="glow"
                style={{
                  position: "absolute",
                  width: "130%",
                  height: "130%",
                  objectFit: "contain",
                  opacity: lightOn ? 1 : 0,
                  transition: "opacity 0.3s",
                }}
              />

              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  border: lightOn ? "2px solid transparent" : "2px solid #2f3338",
                  backgroundColor: "transparent",
                  zIndex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  transition: "all 0.2s ease",
                }}
              >
                <span
                  style={{
                    color: "white",
                    fontSize: "18px",
                  }}
                >
                  <PowerSettingsNewIcon
                    size={28}
                    color={lightOn ? "#00BFFF" : "#FFFFFF"}
                    strokeWidth={2.5}
                  />
                </span>
              </div>
            </div>
          </div>

          <div className="light-mode-buttons"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: "50px",
              marginBottom: "30px",
              width: "100%",
            }}
          >
            <div
              onClick={() => setMode("Single")}
              style={{
                position: "relative",
                width: "160px",
                height: "70px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <img
                src={buttonBlueGlow}
                alt="glow"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  width: "115%",
                  transform: "translate(-50%, -50%)",
                  opacity: mode === "Single" ? 1 : 0,
                  transition: "opacity 0.3s ease",
                }}
              />
              <button
                style={{
                  position: "relative",
                  width: "100%",
                  height: "50px",
                  background: "transparent",
                  border: mode === "Single" ? "none" : "2px solid #2f3338",
                  borderRadius: "30px",
                  color: "white",
                  fontSize: "18px",
                  cursor: "pointer",
                }}
              >
                Single
              </button>
            </div>

            <div
              onClick={() => setMode("Animate")}
              style={{
                position: "relative",
                width: "160px",
                height: "70px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <img
                src={buttonBlueGlow}
                alt="glow"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  width: "115%",
                  transform: "translate(-50%, -50%)",
                  opacity: mode === "Animate" ? 1 : 0,
                  transition: "opacity 0.3s ease",
                }}
              />
              <button
                style={{
                  position: "relative",
                  width: "100%",
                  height: "50px",
                  background: "transparent",
                  border: mode === "Animate" ? "none" : "2px solid #2f3338",
                  borderRadius: "30px",
                  color: "white",
                  fontSize: "18px",
                  cursor: "pointer",
                }}
              >
                Animate
              </button>
            </div>
          </div>

          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {mode === "Single" ? (
              <ControlLightColourWheel
                width={window.innerWidth < 480 ? 240 : 300}
                onColorChange={handleColourChange}
                onBrightnessChange={handleBrightnessChange}
                brightness={brightness}
                colour={colour}
              />
            ) : (
              <ControlLightAnimate
                onModeChange={handleAnimateMode}
                onSpeedChange={handleAnimateSpeedChange}
                onBrightnessChange={handleAnimateBrightnessChange}
                selectedMode={lightMode}
                selectedSpeed={speed}
                selectedBrightness={brightness}
              />
            )}
          </div>
        </div>
      </Paper>
    </>
  );
};

export default ControlLightMode;
